import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';

interface ConfirmDialogContextType {
  confirm: (message: string) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<string | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((msg: string): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setMessage(msg);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setMessage(null);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setMessage(null);
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      {message !== null && (
        <Dialog
          open={true}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) handleCancel();
          }}
        >
          <DialogContent className="w-full max-w-screen-sm rounded shadow-xl!">
            <DialogTitle className="border-b border-gray-200 px-6 py-4 text-base! font-bold! text-gray-900">
              確認
            </DialogTitle>
            <div className="p-6">
              <p className="text-sm text-gray-900">{message}</p>
            </div>
            <div className="flex justify-end gap-4 border-t border-gray-200 px-6 py-4">
              <Button color="secondary" size="md" type="button" onClick={handleCancel}>
                キャンセル
              </Button>
              <Button color="primary" size="md" type="button" onClick={handleConfirm}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return context.confirm;
};
